/* Automatic table of contents, sticky Song index and lyrics interaction controls. */
(() => {
  'use strict';

  const lyricsSelector = '[data-lyrics-protected="true"]';

  const headingLabel = (heading) => (heading.textContent || '').replace(/\s+/g, ' ').trim();

  const shortHeadingLabel = (heading) => {
    const text = heading.querySelector('span:not(.heading-emoji)');
    if (text) {
      for (const node of text.childNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR') break;
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          return node.textContent.replace(/\s+/g, ' ').trim();
        }
      }
    }
    return headingLabel(heading).replace(/^\S+\s*/, '');
  };

  const collectEntries = (editor) => {
    const headings = Array.from(editor.querySelectorAll('.section > h2.section-heading'))
      .filter((heading) => headingLabel(heading));
    headings.forEach((heading, position) => {
      if (!heading.id) heading.id = `lnk_h2_${position + 1}`;
    });
    const entries = headings.map((heading) => ({
      target: heading,
      id: heading.id,
      label: shortHeadingLabel(heading),
      fullLabel: headingLabel(heading),
      emoji: (heading.querySelector('.heading-emoji')?.textContent || '♪').trim()
    }));
    const intro = editor.querySelector('#intro');
    if (intro) {
      entries.unshift({ target: intro, id: intro.id, label: 'イントロ', fullLabel: 'イントロ', emoji: '🎵' });
    }
    return entries;
  };

  const buildSongIndex = (root, entries) => {
    const host = root.querySelector('[data-music-song-index]');
    const list = host?.querySelector('[data-music-song-index-list]');
    if (!host || !list) return;
    list.replaceChildren();
    const links = new Map();
    entries.forEach((entry) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const emoji = document.createElement('span');
      const label = document.createElement('span');
      emoji.className = 'hp-ranking-index__number';
      emoji.setAttribute('aria-hidden', 'true');
      emoji.textContent = entry.emoji;
      label.textContent = entry.label;
      link.href = `#${entry.id}`;
      link.append(emoji, label);
      item.append(link);
      list.append(item);
      links.set(entry.id, link);
    });

    let queued = false;
    const markCurrent = () => {
      queued = false;
      let current = entries[0];
      entries.forEach((entry) => {
        if (entry.target.getBoundingClientRect().top <= 120) current = entry;
      });
      links.forEach((link, id) => {
        if (id === current.id) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };
    const queueCurrent = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(markCurrent);
    };
    window.addEventListener('scroll', queueCurrent, { passive: true });
    markCurrent();
  };

  const buildArticleIndex = (root = document) => {
    const article = root.querySelector('.pages-article article');
    const title = article?.querySelector(':scope > h1');
    const editor = article?.querySelector('#article_editor');
    if (!article || !title || !editor) return;
    const entries = collectEntries(editor);
    const headings = entries.filter((entry) => entry.target.matches('h2'));
    if (!headings.length) return;
    buildSongIndex(root, entries);
    if (article.querySelector('#article_index')) return;

    const index = document.createElement('div');
    index.id = 'article_index';
    index.className = 'c-article_index u-mt_pc--50 u-mt_sp--30 u-pd_pc--35 u-pd_sp--25 u-border--secondary u-border_width--1 u-border_color--primary u-border_style--solid';

    const boxTitle = document.createElement('div');
    boxTitle.className = 'box_title';
    const indexTitle = document.createElement('h2');
    indexTitle.className = 'index_title u-fs--s u-fw--400';
    indexTitle.textContent = '目次';

    const toggle = document.createElement('button');
    toggle.className = 'close u-fs--ss u-fw--400';
    toggle.type = 'button';
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-controls', 'music-article-index-list');
    toggle.append('［ ');
    const closeLabel = document.createElement('em');
    closeLabel.className = 'cl';
    closeLabel.textContent = '閉じる';
    const openLabel = document.createElement('em');
    openLabel.className = 'op';
    openLabel.textContent = '開く';
    toggle.append(closeLabel, openLabel, ' ］');
    boxTitle.append(indexTitle, toggle);

    const listBox = document.createElement('div');
    listBox.id = 'music-article-index-list';
    listBox.className = 'box_article_index c-plaintext--primary u-fc--senary u-mt_pc--15 u-mt_sp--10';
    const list = document.createElement('ul');
    headings.forEach((entry) => {
      const item = document.createElement('li');
      item.className = 'index_h2';
      const link = document.createElement('a');
      link.className = 'over noscroll article_scroll';
      link.href = `#${entry.id}`;
      link.textContent = entry.fullLabel;
      item.append(link);
      list.append(item);
    });
    listBox.append(list);
    index.append(boxTitle, listBox);
    const alias = title.nextElementSibling;
    const indexAnchor = alias?.matches('.music-artist-alias') ? alias : title;
    indexAnchor.insertAdjacentElement('afterend', index);

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.classList.toggle('is-close', !open);
      toggle.setAttribute('aria-expanded', String(open));
      listBox.hidden = !open;
    });
  };

  const protectedTarget = (target) => target instanceof Element ? target.closest(lyricsSelector) : null;

  const protectedSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const node = selection.anchorNode;
    const element = node && (node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement);
    return protectedTarget(element);
  };

  const blockLyricsAction = (event) => {
    if (protectedTarget(event.target) || protectedSelection()) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const blockLyricsShortcut = (event) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    if (!['a', 'c', 'x'].includes(event.key.toLowerCase())) return;
    blockLyricsAction(event);
  };

  const initializeLyricsProtection = (root = document) => {
    root.querySelectorAll(lyricsSelector).forEach((region) => {
      if (region.dataset.lyricsProtectionReady === 'true') return;
      region.dataset.lyricsProtectionReady = 'true';
      ['contextmenu', 'copy', 'cut', 'dragstart', 'selectstart'].forEach((eventName) => {
        region.addEventListener(eventName, blockLyricsAction);
      });
      region.addEventListener('keydown', blockLyricsShortcut);
    });
  };

  const youtubeVideoId = (iframe) => {
    try {
      const url = new URL(iframe.src, window.location.origin);
      const match = url.pathname.match(/\/embed\/([^/?]+)/);
      return match ? match[1] : '';
    } catch (error) {
      return '';
    }
  };

  const initializeYoutubePosters = (root = document) => {
    root.querySelectorAll('.c-iframe_youtube iframe[src*="youtube.com/embed/"]').forEach((iframe) => {
      const wrapper = iframe.closest('.c-iframe_youtube');
      const videoId = youtubeVideoId(iframe);
      if (!wrapper || !videoId || wrapper.dataset.youtubePosterReady === 'true') return;
      wrapper.dataset.youtubePosterReady = 'true';

      const title = iframe.title || 'YouTube動画';
      const sources = [
        `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
      ];
      let sourceIndex = 0;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'music-youtube-poster';
      button.setAttribute('aria-label', `${title}を再生`);

      const image = document.createElement('img');
      image.src = sources[sourceIndex];
      image.alt = '';
      image.width = 1280;
      image.height = 720;
      image.decoding = 'async';

      const tryNextSource = () => {
        if (sourceIndex >= sources.length - 1) return;
        sourceIndex += 1;
        image.src = sources[sourceIndex];
      };
      image.addEventListener('error', tryNextSource);
      image.addEventListener('load', () => {
        if (image.naturalWidth <= 120) tryNextSource();
      });

      const play = document.createElement('span');
      play.className = 'music-youtube-poster__play';
      play.setAttribute('aria-hidden', 'true');
      button.append(image, play);

      button.addEventListener('click', () => {
        const url = new URL(iframe.src, window.location.origin);
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('playsinline', '1');
        iframe.src = url.toString();
        iframe.loading = 'eager';
        wrapper.replaceChildren(iframe);
      }, { once: true });

      wrapper.replaceChildren(button);
    });
  };

  const initializeCardDates = (root) => {
    const source = document.querySelector('[data-music-card-dates]');
    if (!source) return;
    let dates;
    try { dates = JSON.parse(source.textContent); } catch (_) { return; }
    if (!dates || typeof dates !== 'object') return;

    root.querySelectorAll('.hp-ranking .article-related-manga__item').forEach((card) => {
      const link = card.querySelector('a.article-related-manga__link');
      const title = card.querySelector('.article-related-manga__title');
      if (!link || !title) return;
      let url;
      try { url = new URL(link.getAttribute('href'), window.location.origin); } catch (_) { return; }
      if (url.origin !== window.location.origin) return;
      const date = dates[url.pathname];
      if (!date || typeof date.datetime !== 'string' || !/^\d{4}\/\d{2}\/\d{2}$/.test(date.display)) return;
      let paragraph = card.querySelector('.article-related-manga__date');
      if (!paragraph) {
        paragraph = document.createElement('p');
        paragraph.className = 'article-related-manga__date c-plaintext--quaternary u-fc--senary blog_date';
      }
      const small = document.createElement('small');
      const time = document.createElement('time');
      time.dateTime = date.datetime;
      time.textContent = date.display;
      small.append(time);
      paragraph.replaceChildren(small);
      title.before(paragraph);
    });
  };

  const initialize = (root = document) => {
    buildArticleIndex(root);
    initializeLyricsProtection(root);
    initializeYoutubePosters(root);
    initializeCardDates(root);
  };

  window.MusicArticle = { init: initialize };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
